/**
 * NewPageModal - Create a new page from inside the Page Builder
 * 
 * Flow:
 * 1. Enter title + slug
 * 2. Pick a template (optional)
 * 3. Create page via API (registers in canonical registry + creates PB definition)
 * 4. Navigate to builder for the new page
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, LayoutGrid, Columns, Image, Type } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PAGE_TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank Page',
    icon: FileText,
    description: 'Start with an empty section',
    blocks: [
      {
        id: 'section_1',
        type: 'section',
        enabled: true,
        order: 0,
        props: {
          backgroundColor: 'transparent',
          paddingTop: 48,
          paddingBottom: 48,
          maxWidth: '1280px',
        },
        layout: { preset: 'single' },
      },
    ],
  },
  {
    id: 'hero_content',
    name: 'Hero + Content',
    icon: LayoutGrid,
    description: 'Hero section with content below',
    blocks: [
      {
        id: 'hero_1',
        type: 'landing_hero',
        _templateId: 'landing_hero',
        enabled: true,
        order: 1,
        props: {
          earlyAccessLabel: 'New Page',
          tagline: 'Your tagline here',
          primaryCtaLabel: 'Get Started',
          primaryCtaDestination: '#',
          secondaryCtaLabel: 'Learn More',
          secondaryCtaDestination: '#',
        },
        layout: { preset: 'single' },
      },
      {
        id: 'content_1',
        type: 'landing_benefits',
        _templateId: 'landing_benefits',
        enabled: true,
        order: 2,
        props: {
          heading: 'Why Choose Us?',
          items: [
            { title: 'Feature 1', description: 'Description here', icon: 'eye' },
            { title: 'Feature 2', description: 'Description here', icon: 'zap' },
            { title: 'Feature 3', description: 'Description here', icon: 'target' },
          ],
        },
        layout: { preset: 'single' },
      },
    ],
  },
  {
    id: 'gallery',
    name: 'Gallery',
    icon: Image,
    description: 'Image gallery layout',
    blocks: [
      {
        id: 'hero_1',
        type: 'landing_hero',
        _templateId: 'landing_hero',
        enabled: true,
        order: 1,
        props: {
          earlyAccessLabel: 'Gallery',
          tagline: 'Explore our collection',
          primaryCtaLabel: 'View All',
          primaryCtaDestination: '#',
        },
        layout: { preset: 'single' },
      },
    ],
  },
  {
    id: 'text_page',
    name: 'Text Page',
    icon: Type,
    description: 'Simple text content page',
    blocks: [
      {
        id: 'hero_1',
        type: 'landing_hero',
        _templateId: 'landing_hero',
        enabled: true,
        order: 1,
        props: {
          earlyAccessLabel: '',
          tagline: 'Page description goes here',
          primaryCtaLabel: '',
          primaryCtaDestination: '',
        },
        layout: { preset: 'single' },
      },
    ],
  },
];

const NewPageModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('blank');
  const [creating, setCreating] = useState(false);
  
  // Auto-generate slug from title
  const handleTitleChange = (value) => {
    setTitle(value);
    const autoSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    setSlug(autoSlug);
  };
  
  const handleCreate = useCallback(async () => {
    if (!title.trim() || !slug.trim()) {
      toast.error('Title and slug are required');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const template = PAGE_TEMPLATES.find(t => t.id === selectedTemplate);
      const blocks = template?.blocks || [];
      
      // Create the page definition
      const response = await axios.post(
        `${API_URL}/api/admin/page-builder/pages`,
        {
          page_id: slug,
          name: title,
          description: `Custom page: ${title}`,
          route: `/${slug}`,
          blocks,
          visibility: 'public',
          status: 'draft',
        },
        { headers }
      );
      
      // Register in canonical page registry
      await axios.post(
        `${API_URL}/api/admin/page-registry`,
        {
          pageId: slug,
          title,
          slug: `/${slug}`,
          pageType: 'CUSTOM',
          visibility: 'public',
          status: 'published',
          is_dynamic: false,
        },
        { headers }
      );
      
      toast.success(`Page "${title}" created!`);
      onClose();
      
      // Reset form
      setTitle('');
      setSlug('');
      setSelectedTemplate('blank');
      
      // Navigate to editor for the new page
      navigate(`/admin/page-builder/${slug}`);
    } catch (error) {
      console.error('Error creating page:', error);
      const msg = error.response?.data?.detail || 'Failed to create page';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }, [title, slug, selectedTemplate, navigate, onClose]);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-orange-500" />
            Create New Page
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Create a new page and start editing it in the Page Builder.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Title */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm">Page Title</Label>
            <Input
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="My New Page"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="new-page-title-input"
            />
          </div>
          
          {/* Slug */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm">URL Slug</Label>
            <div className="flex items-center gap-1">
              <span className="text-zinc-500 text-sm">/</span>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="my-new-page"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="new-page-slug-input"
              />
            </div>
          </div>
          
          {/* Template picker */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm">Template</Label>
            <div className="grid grid-cols-2 gap-2">
              {PAGE_TEMPLATES.map(template => {
                const Icon = template.icon;
                const isSelected = selectedTemplate === template.id;
                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    data-testid={`template-${template.id}`}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-blue-400' : 'text-zinc-500'}`} />
                    <p className={`text-xs font-medium ${isSelected ? 'text-blue-300' : 'text-zinc-300'}`}>
                      {template.name}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{template.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !title.trim() || !slug.trim()}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="new-page-create-btn"
          >
            {creating ? 'Creating...' : 'Create Page'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewPageModal;
